/**
 * @fileoverview Firm Contact Link Picker
 *
 * Purpose: Searchable picker that lets the practitioner attach a
 * `jobContacts` row to a curated `firmContacts` row so empty job-level
 * fields fall back to the firm-level values.
 *
 * Behaviour:
 * - Pulls the firm directory via `api.contacts.queries.listFirmContacts`
 *   scoped to the contact's org.
 * - Filters out archived rows.
 * - `onPick` fires with the chosen `firmContactId`. The caller calls
 *   `linkJobContactToFirm` and closes the popover.
 */

import { useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { Badge } from "@/ui/primitives/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";
import { FIRM_CATEGORY_META, formatAbn, formatAcn } from "./types";

interface FirmContactLinkPickerProps {
  orgId: Id<"organizations">;
  currentFirmContactId?: Id<"firmContacts">;
  onPick: (firmContactId: Id<"firmContacts">) => void;
  disabled?: boolean;
}

export function FirmContactLinkPicker({
  orgId,
  currentFirmContactId,
  onPick,
  disabled,
}: FirmContactLinkPickerProps) {
  const [open, setOpen] = useState(false);

  // The picker is opened from inside the contact-detail Sheet. Without
  // the explicit `container`, Radix portals the popover to body — which
  // breaks scroll-wheel events because they bubble into the Sheet and
  // get absorbed. We render inside the Sheet's dialog container so
  // wheel events stay local to the popover.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogContainer =
    triggerRef.current?.closest<HTMLElement>("[role='dialog']") ?? undefined;

  const { data: firmContacts, isLoading } = useQuery({
    ...convexQuery(api.contacts.queries.listFirmContacts, { orgId }),
    enabled: open,
  });

  const eligible = (firmContacts ?? []).filter((f) => f.status === "active");

  return (
    // Use Radix's default `modal` Popover (not modal={false}) so the
    // popover has a focus-managed dismissable layer — clicks inside the
    // popover stay inside, only Escape / outside-click dismisses.
    // Pattern mirrors src/components/settings/precedents/CanonicalKeyPicker.tsx.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex h-8 shrink-0 items-center justify-between gap-2 px-3 text-xs",
            "rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-ring",
            "transition-colors hover:bg-muted/30",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <span
            className={cn(
              "truncate text-left",
              !currentFirmContactId && "text-muted-foreground",
            )}
          >
            {currentFirmContactId
              ? "Change firm link…"
              : "Link to firm contact"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[440px] p-0"
        align="end"
        // Stop wheel events from bubbling into the parent Sheet — without
        // this the Sheet absorbs the scroll and the popover list won't
        // scroll past its visible items.
        onWheel={(e) => e.stopPropagation()}
        container={dialogContainer}
      >
        <Command
          // Plain substring filter — feels like a regular text search
          // rather than cmdk's looser fuzzy/typo-tolerant scoring. The
          // `value` we set on each CommandItem already concatenates name
          // + email + ABN + ACN, so a single substring check is enough.
          filter={(value, search) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            return value.toLowerCase().includes(q) ? 1 : 0;
          }}
          // Stop arrow keys / typing from bubbling out of the popover
          // and triggering things in the Sheet.
          onKeyDown={(e) => e.stopPropagation()}
        >
          <CommandInput placeholder="Search by name, email, ABN, ACN…" />
          <CommandList className="max-h-[340px] overscroll-contain">
            {isLoading ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                Loading firm contacts…
              </div>
            ) : (
              <CommandEmpty>
                No firm contacts match your search. Add one from the Firm
                Contacts directory.
              </CommandEmpty>
            )}
            {eligible.map((f) => {
              const meta = FIRM_CATEGORY_META[f.category];
              const Icon = meta.icon;
              const isCurrent = f._id === currentFirmContactId;
              return (
                <CommandItem
                  key={f._id}
                  value={`${f.displayName} ${f.email ?? ""} ${f.abn ?? ""} ${f.acn ?? ""}`}
                  onSelect={() => {
                    if (!isCurrent) onPick(f._id);
                    setOpen(false);
                  }}
                  className="items-start gap-2 px-3 py-2.5"
                >
                  <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-foreground">
                        {f.displayName}
                      </span>
                      {isCurrent && (
                        <Badge
                          color="emerald"
                          variant="soft"
                          size="sm"
                          className="px-1 py-0 text-[10px]"
                        >
                          linked
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <Badge
                        color={meta.badgeColor}
                        variant="soft"
                        size="sm"
                        className="px-1 py-0 text-[10px]"
                      >
                        {meta.shortLabel}
                      </Badge>
                      {f.acn && (
                        <span className="font-mono text-[10px] tabular-nums text-muted-foreground/80">
                          ACN {formatAcn(f.acn)}
                        </span>
                      )}
                      {f.abn && (
                        <span className="font-mono text-[10px] tabular-nums text-muted-foreground/80">
                          ABN {formatAbn(f.abn)}
                        </span>
                      )}
                      {f.email && (
                        <span className="max-w-[160px] truncate text-[10px] text-muted-foreground/80">
                          {f.email}
                        </span>
                      )}
                    </div>
                  </div>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
